import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { AdminConfig } from '@baex/shared';
import { config as configApi } from '../../api/endpoints';
import { FALLBACK_TIMEZONE } from '../../lib/format';
import { TimezoneProvider } from '../../lib/timezone';

export const ADMIN_CONFIG_QUERY_KEY = ['admin-config'] as const;

/**
 * Fetches `GET /admin/api/config` once per signed-in session and publishes the runtime
 * display settings to the tree. The value only changes when the API restarts, so it is
 * cached indefinitely rather than revalidated; until it lands, the fallback zone applies.
 */
export function useAdminConfig() {
  return useQuery<AdminConfig>({
    queryKey: ADMIN_CONFIG_QUERY_KEY,
    queryFn: () => configApi.get(),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

export function AdminConfigProvider({ children }: { children: ReactNode }) {
  const config = useAdminConfig();
  return (
    <TimezoneProvider timezone={config.data?.dashboardTimezone ?? FALLBACK_TIMEZONE}>
      {children}
    </TimezoneProvider>
  );
}
